<template>
  <div class="overlay">
    <video ref="video" autoplay muted playsinline style="z-index: -1;"></video>
    <canvas ref="videoCanvas"></canvas>
    <canvas v-show="!showMask" ref="canvas"></canvas>
    <img
      v-if="!frontImage"
      v-show="showMask"
      src="@/assets/MobileScan/body-shape.svg"
      alt="body_shape"
      class="body-shape"
    />
    <img
      v-else
      v-show="showMask"
      src="@/assets/MobileScan/body-shape-side.svg"
      alt="body_shape"
      class="body-shape"
    />
    <div class="border top-left"></div>
    <div class="border top-right"></div>
    <div class="border bottom-left"></div>
    <div class="border bottom-right"></div>
  </div>
</template>

<script>
import throttle from 'lodash/throttle';
import { wait } from '@/modules/utils';
import PoseDetector from '@/modules/PoseDetector';
import PoseUtils from '@/modules/PoseUtils';
import FPSLimiter from '@/modules/FPSLimiter';
import { createScanOverlay } from '@/modules/scan-overlay';
import audioPlayer from '@/mixins/audio-player';

// 錯誤碼（見 PoseUtils 的 IsFrontPose 與 IsLeftPose）對應到要標記的關節點，
// MediaPipe Pose 的索引：13/15 左肘腕、14/16 右肘腕、25/26 膝、27/28 踝。
const ERROR_JOINTS = {
  4: [27, 28], // 腳沒開到肩寬
  7: [13, 14, 15, 16], // 兩手都沒張開
  8: [13, 14, 15, 16], // 兩手肘沒伸直
  17: [13, 15], // 左手臂
  18: [13, 15],
  27: [14, 16], // 右手臂
  28: [14, 16],
  10: [16], // 側面：手沒貼髖（IsLeftPose 只看右側）
  11: [26], // 側面：膝沒伸直
};

export default {
  mixins: [audioPlayer],
  data() {
    return {
      frontImage: null,
      sideImage: null,
      frontLandmarks: null,
      sideLandmarks: null,
      frontHeight: null,
      stream: null,
      drawCamera: false,

      stop: false,
      fpsLimiter: new FPSLimiter(30),
      showMask: true,
      isReady: false,
      isPoseError: false,
    };
  },
  mounted() {
    this.overlay = createScanOverlay(this.$refs.canvas);
    this.maskSize = { width: 0, height: 0 };
    this.confidence = null;
    this.lastState = null;

    this.startCamera();
    this.$refs.video.oncanplay = () => {
      this.$emit('oncanplay', this.$refs.video);
      this.setSize(this.$refs.video.videoWidth, this.$refs.video.videoHeight);
      this.drawCamera = true;
      this.drawCameraView();
      this.start();
    };
    // 不少裝置在串流開始後才升到目標解析度，只在 oncanplay 抓一次的話剪影會整片偏離人體
    this.$refs.video.addEventListener('resize', () => {
      this.setSize(this.$refs.video.videoWidth, this.$refs.video.videoHeight);
    });

    this.throttledPlayAudio = throttle(
      key => {
        this.stopAllAudio();
        this.playAudio(key, true);
      },
      6000,
      { trailing: false }
    );

    this.errorAudioKeys = {
      1: 'ms-error-01-keep_close',
      2: 'ms-error-02-keep_far',
      3: 'ms-error-03-keep_center',
      4: 'ms-error-04-shoulder_feet',
      5: 'ms-error-05-stand_straight',
      6: 'ms-error-06-keep_front',
      7: 'ms-error-07-arm_open',
      17: 'ms-error-07-arm_open',
      27: 'ms-error-07-arm_open',
      8: 'ms-error-07-arm_open',
      18: 'ms-error-07-arm_open',
      28: 'ms-error-07-arm_open',

      9: 'ms-error-09-keep_left',
      10: 'ms-error-10-keep_arm',
      11: 'ms-error-11-keep_leg',
    };
  },
  methods: {
    startCamera() {
      navigator.mediaDevices
        .getUserMedia({
          audio: false,
          video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: 'user' },
        })
        .then(stream => {
          this.stream = stream;
          this.$refs.video.srcObject = stream;
        })
        .catch(e => {
          console.log(`[ERROR] startCamera failed: ${e.message}`);
        });
    },
    drawCameraView() {
      if (!this.drawCamera) return;
      const canvas = this.$refs.videoCanvas;
      const ctx = this.$refs.videoCanvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(this.$refs.video, 0, 0);
      requestAnimationFrame(() => this.drawCameraView());
    },
    takePicture(isFront = true) {
      this.stop = true;
      if (isFront) {
        this.frontLandmarks = PoseDetector.getResults()?.poseLandmarks;
        this.frontHeight = this.frontLandmarks ? PoseUtils.GetHeight(this.frontLandmarks) : null;
      } else {
        this.sideLandmarks = PoseDetector.getResults()?.poseLandmarks;
      }
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      const { video } = this.$refs;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0);
      return new Promise(resolve => {
        canvas.toBlob(
          blob => {
            try {
              const url = URL.createObjectURL(blob);
              if (isFront) this.frontImage = url;
              else this.sideImage = url;
            } catch (error) {
              console.log(`[ERROR] takePicture failed: ${error.message}`);
            }
            resolve();
          },
          'image/jpeg',
          0.95
        );
      });
    },
    resetPoseState() {
      this.showMask = true;
      this.isReady = false;
      this.isPoseError = false;
    },
    start() {
      this.stop = false;
      this.resetPoseState();
      // 清掉時間平滑的歷史與上一段讀回的遮罩，剪影才不會殘留上一段的影子
      this.overlay.reset();
      this.confidence = null;
      this.lastState = null;

      wait(5).then(() => {
        this.fixUpdate();
      });
    },
    setSize(width, height) {
      const { videoCanvas } = this.$refs;
      // overlay 會自己設遮罩 canvas 的 width / height
      this.overlay.resize(width, height);
      if (videoCanvas) {
        videoCanvas.width = width;
        videoCanvas.height = height;
      }
    },
    playErrorSound(errorCode) {
      if (this.errorAudioKeys[errorCode]) {
        this.throttledPlayAudio(this.errorAudioKeys[errorCode]);
      }
    },
    parseLandmarks() {
      const { poseLandmarks } = PoseDetector.getResults();

      if (!Array.isArray(poseLandmarks)) {
        this.resetPoseState();
        return;
      }
      this.showMask = false;

      let errorCode = -1;
      let isVisible = false;
      if (!this.frontImage) {
        errorCode = PoseUtils.IsFrontPose(poseLandmarks);
        isVisible = PoseUtils.IsMainLandmarksVisible(poseLandmarks, 0.9);
      } else {
        errorCode = PoseUtils.IsLeftPose(poseLandmarks, this.frontHeight);
        isVisible = PoseUtils.IsRightLandmarksVisible(poseLandmarks, 0.8);
      }

      this.isReady = isVisible && errorCode === 0;
      this.isPoseError = !this.isReady;
      this.playErrorSound(errorCode);
      this.drawOverlay(errorCode);
    },
    /**
     * 把遮罩讀成 overlay 認的 Uint8Array 信心值。
     *
     * 舊版 Solutions 給的 segmentationMask 是 drawImage 相容的物件，不是 Uint8Array，
     * 所以要先畫到離屏畫布再讀回像素。人形在 alpha 通道。
     */
    readMaskConfidence(mask) {
      if (!mask || !mask.width || !mask.height) {
        this.confidence = null;
        return;
      }

      const { width, height } = mask;
      if (
        !this.maskReader ||
        this.maskReader.width !== width ||
        this.maskReader.height !== height
      ) {
        this.maskReader = document.createElement('canvas');
        this.maskReader.width = width;
        this.maskReader.height = height;
        this.maskCtx = this.maskReader.getContext('2d', { willReadFrequently: true });
        this.maskBuffer = new Uint8Array(width * height);
      }
      this.maskSize = { width, height };

      this.maskCtx.clearRect(0, 0, width, height);
      this.maskCtx.drawImage(mask, 0, 0);
      const { data } = this.maskCtx.getImageData(0, 0, width, height);
      for (let i = 0; i < this.maskBuffer.length; i += 1) {
        this.maskBuffer[i] = data[i * 4 + 3];
      }
      this.confidence = this.maskBuffer;
    },
    drawOverlay(errorCode) {
      const results = PoseDetector.getResults();
      const hasNewResults = PoseDetector.consumeNewResults();
      const state = this.isReady ? 'default' : 'error';

      // 推論只有 10fps，這個迴圈是 30fps。就緒狀態的畫面是靜態的，遮罩與狀態都沒變
      // 就跟上一格一模一樣，整趟省掉；錯誤狀態的關節點要脈動，得繼續逐格重畫。
      const isStatic = state === 'default' && state === this.lastState;
      if (!hasNewResults && isStatic) return;
      this.lastState = state;

      // 遮罩沒換就沿用上一次的信心值，省掉 getImageData 的 GPU→CPU 同步讀回
      if (hasNewResults) {
        this.readMaskConfidence(results.segmentationMask);
      }

      this.overlay.render({
        confidence: this.confidence,
        maskWidth: this.maskSize.width,
        maskHeight: this.maskSize.height,
        landmarks: results.poseLandmarks,
        state,
        // 沒對應到的錯誤碼傳 undefined，overlay 會落回預設的肘腕膝踝 8 點
        errorJoints: ERROR_JOINTS[errorCode],
      });
    },
    fixUpdate() {
      if (this.stop) return;
      if (this.fpsLimiter.update()) {
        try {
          this.parseLandmarks();
        } catch (e) {
          this.resetPoseState();
          console.log(`[ERROR] fixUpdate failed: ${e.message}`);
        }
      }
      requestAnimationFrame(this.fixUpdate);
    },
  },
  beforeDestroy() {
    this.drawCamera = false;
    this.stop = true;
    if (this.stream) {
      const tracks = this.stream.getTracks();
      tracks.forEach(track => track.stop());
    }
  },
  watch: {
    isReady(nv) {
      this.$emit('onReady', nv);
    },
    isPoseError(nv) {
      this.$emit('onError', nv);
    },
  },
};
</script>

<style lang="scss" scoped>
.overlay {
  @apply w-full h-full relative;
  video {
    position: absolute;
    transform: scale(-1, 1);
    object-fit: cover;
    width: 100%;
    height: 100%;
  }
  canvas,
  .body-shape {
    position: absolute;
    transform: scale(-1, 1);
    object-fit: cover;
    width: 100%;
    height: 100%;
  }
  .border {
    position: absolute;
    height: 40px;
    width: 40px;
    border-radius: 4px;
    border: 3px solid rgba(109, 101, 255, 1);
  }
  .top-left {
    top: -1.5px;
    left: -1.5px;
    border-right: 0px;
    border-bottom: 0px;
  }
  .top-right {
    top: -1.5px;
    right: -1.5px;
    border-left: 0px;
    border-bottom: 0px;
  }
  .bottom-left {
    bottom: -1.5px;
    left: -1.5px;
    border-top: 0px;
    border-right: 0px;
  }
  .bottom-right {
    bottom: -1.5px;
    right: -1.5px;
    border-top: 0px;
    border-left: 0px;
  }
}
</style>
